import { api } from './api';
import { Product, PaginatedResponse } from '../types';

export interface CreateProductDTO {
  ean: string;
  name: string;
  description?: string;
  brandId: string;
  price: number;
  originalPrice?: number;
  imageUrl?: string;
  iconIds?: string[];
}

export interface UpdateProductDTO extends Partial<CreateProductDTO> {}

export interface ProductFilters {
  brandId?: string;
  search?: string;
  page?: number;
  limit?: number;
  isActive?: boolean;
}

// Vysledek synchronizace vyprodanych. Letaky jsou v odpovedi kvuli navazne akci:
// jejich ulozene PDF ma po zmene soldOut zastaraly vodoznak a je potreba ho pregenerovat.
export interface SyncSoldOutFlyer {
  id: string;
  name: string;
  validFrom: string;
  validTo: string;
  supplierName: string | null;
  supplierEmail: string | null;
}

export interface SyncSoldOutProduct {
  id: string;
  name: string;
  eanCode: string;
  flyers: SyncSoldOutFlyer[];
}

export interface SyncSoldOutResult {
  success: boolean;
  message: string;
  markedSoldOutCount: number;
  reactivatedCount: number;
  totalUpdated: number;
  discontinuedProducts: SyncSoldOutProduct[];
  reactivatedProducts: SyncSoldOutProduct[];
}

export const productsService = {
  async getProducts(filters?: ProductFilters): Promise<PaginatedResponse<Product>> {
    const response = await api.get<PaginatedResponse<Product>>('/products', { params: filters });
    return response.data;
  },

  async getProduct(id: string): Promise<Product> {
    const response = await api.get<Product>(`/products/${id}`);
    return response.data;
  },

  async createProduct(data: CreateProductDTO): Promise<Product> {
    const response = await api.post<Product>('/products', data);
    return response.data;
  },

  async updateProduct(id: string, data: UpdateProductDTO): Promise<Product> {
    const response = await api.patch<Product>(`/products/${id}`, data);
    return response.data;
  },

  async deleteProduct(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  async getMyProducts(): Promise<Product[]> {
    const response = await api.get<PaginatedResponse<Product>>('/products');
    return response.data.data; // Extract products array from paginated response
  },

  async getMyProductsStats(): Promise<{ total: number; active: number }> {
    const response = await api.get<PaginatedResponse<Product>>('/products', {
      params: { limit: 1 } // Get minimal data, we only need the count
    });

    // Count active products from total
    const activeResponse = await api.get<PaginatedResponse<Product>>('/products', {
      params: { limit: 1, isActive: true }
    });

    return {
      total: response.data.meta?.total || 0,
      active: activeResponse.data.meta?.total || 0
    };
  },



  async validateEAN(
    ean: string,
    price?: number,
    originalPrice?: number,
  ): Promise<{
    ean: string;
    found: boolean;
    pricesMatch: boolean;
    erpPrice?: number;
    erpOriginalPrice?: number;
    erpProductName?: string;
    erpBrand?: string;
    erpCategoryCode?: string;
    erpInstallationType?: 'BUILT_IN' | 'FREESTANDING' | null;
  }> {
    const params: any = {};
    if (price !== undefined) params.price = price.toString();
    if (originalPrice !== undefined) params.originalPrice = originalPrice.toString();

    const response = await api.get<{
      ean: string;
      found: boolean;
      pricesMatch: boolean;
      erpPrice?: number;
      erpOriginalPrice?: number;
      erpProductName?: string;
      erpBrand?: string;
      erpCategoryCode?: string;
    erpInstallationType?: 'BUILT_IN' | 'FREESTANDING' | null;
    }>(`/products/${ean}/validate-ean`, { params });
    return response.data;
  },

  async checkDuplicateEan(ean: string): Promise<{
    exists: boolean;
    count: number;
    latestProduct: Product | null;
    allProducts: Product[];
    existsInOtherBrand?: boolean;
    otherBrandName?: string | null;
  }> {
    const response = await api.get<{
      exists: boolean;
      count: number;
      latestProduct: Product | null;
      allProducts: Product[];
      existsInOtherBrand?: boolean;
      otherBrandName?: string | null;
    }>(`/products/check-duplicate-ean/${ean}`);
    return response.data;
  },

  async getActiveFlyersProducts(): Promise<ActiveFlyerProduct[]> {
    const response = await api.get<ActiveFlyerProduct[]>('/products/active-flyers/products');
    return response.data;
  },

  async markDiscontinuedAsSoldOut(): Promise<SyncSoldOutResult> {
    const response = await api.post('/products/active-flyers/mark-discontinued-sold-out');
    return response.data;
  },
};

export interface ActiveFlyerProduct {
  id: string;
  name: string;
  eanCode: string;
  price: number | string;
  originalPrice: number | string | null;
  soldOut: boolean;
  brandName: string;
  brandColor?: string | null;
  categoryName: string | null;
  flyers: { id: string; name: string; validFrom: string; validTo: string }[];
  discontinued: boolean;
}
